import React, { useState, useEffect, useCallback } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/api';

interface BoletoEligibilityInfoProps {
  invoice: Invoice;
}

interface EligibilityInfo {
  eligible: boolean;
  currency_is_brl: boolean;
  customer_has_tax_id: boolean;
  customer_tax_id: string | null;
  invoice_status: string;
  existing_boleto: any;
  reasons: string[];
}

const BoletoEligibilityInfo: React.FC<BoletoEligibilityInfoProps> = ({ invoice }) => {
  const [eligibilityInfo, setEligibilityInfo] = useState<EligibilityInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkEligibility = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.checkBoletoEligibility(invoice.invoice_id);
      
      // Analyze the response and build user-friendly reasons
      const reasons: string[] = [];
      
      if (invoice.invoice_status === 'draft') {
        reasons.push('Invoice must be finalized (not in draft status)');
      }
      
      if (!response.currency_is_brl) {
        reasons.push(`Currency must be BRL (current: ${response.currency})`);
      }
      
      if (!response.customer_has_tax_id) {
        reasons.push('Customer must have a valid tax ID (CPF/CNPJ)');
      }
      
      if (response.existing_boleto) {
        reasons.push(`Boleto already exists (Status: ${response.existing_boleto.status})`);
      }
      
      setEligibilityInfo({
        ...response,
        reasons
      });
      
    } catch (err: any) {
      console.error('Error checking boleto eligibility:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to check boleto eligibility');
    } finally {
      setLoading(false);
    }
  }, [invoice.invoice_id, invoice.invoice_status]);

  useEffect(() => {
    checkEligibility();
  }, [checkEligibility]);

  if (loading) {
    return (
      <div className="alert alert-info">
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          Checking boleto eligibility...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <strong>⚠️ Unable to check boleto eligibility:</strong> {error}
      </div>
    );
  }

  if (!eligibilityInfo) {
    return null;
  }

  // If eligible and no existing boleto, show success message
  if (eligibilityInfo.eligible && !eligibilityInfo.existing_boleto) {
    return (
      <div className="alert alert-success">
        <strong>✅ Boleto Creation Available</strong>
        <p className="mb-0 mt-1">This invoice is eligible for boleto payment. Click "Create Boleto" to generate a payment slip.</p>
      </div>
    );
  }

  // If existing boleto, show info
  if (eligibilityInfo.existing_boleto) {
    return (
      <div className="alert alert-info">
        <strong>ℹ️ Boleto Already Exists</strong>
        <p className="mb-1 mt-1">
          A boleto payment already exists for this invoice with status: 
          <span className="badge bg-secondary ms-1">{eligibilityInfo.existing_boleto.status}</span>
        </p>
        <small className="text-muted">
          Created: {new Date(eligibilityInfo.existing_boleto.created_at).toLocaleString()}
        </small>
      </div>
    );
  }

  // If not eligible, show reasons
  return (
    <div className="alert alert-warning">
      <strong>⚠️ Boleto Creation Not Available</strong>
      <p className="mb-2 mt-1">This invoice is not eligible for boleto payment for the following reasons:</p>
      <ul className="mb-0">
        {eligibilityInfo.reasons.map((reason, index) => (
          <li key={index}>{reason}</li>
        ))}
      </ul>
      {eligibilityInfo.reasons.some(r => r.includes('draft')) && (
        <div className="mt-2">
          <small className="text-muted">
            💡 <strong>Tip:</strong> Finalize this invoice first by clicking "Post Invoice" to enable boleto creation.
          </small>
        </div>
      )}
      {eligibilityInfo.reasons.some(r => r.includes('tax ID')) && (
        <div className="mt-2">
          <small className="text-muted">
            💡 <strong>Tip:</strong> Add a valid CPF or CNPJ to the customer profile to enable boleto payments.
          </small>
        </div>
      )}
      {eligibilityInfo.reasons.some(r => r.includes('BRL')) && (
        <div className="mt-2">
          <small className="text-muted">
            💡 <strong>Tip:</strong> Boleto payments are only available for invoices in Brazilian Real (BRL).
          </small>
        </div>
      )}
    </div>
  );
};

export default BoletoEligibilityInfo;